import React, { useState, useRef } from "react";
import { Form, Button, Card, Row, Col, Spinner } from "react-bootstrap";
import { uploadDocumentRequest, uploadDocumentFilesRequest, deleteDocumentRequest } from "@/services";
import DocumentFiles from '../document_files';
import { uploadEachFile, describeUploadFailures } from '../upload_utils';
import { BsTrash } from "react-icons/bs";
import { useParams } from "react-router-dom";

const EMPTY_MARKETPLACE = { marketplace: "", country: "" };

const MarketplaceStatementsUploadForm = ({
  doc,
  reloadDocumentsList,
  documentsList = [],
  onUploadingChange,
}) => {
  const { applicationId: applicationUuid } = useParams();
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [newMarketplace, setNewMarketplace] = useState(EMPTY_MARKETPLACE);
  const [error, setError] = useState("");
  const [cardError, setCardError] = useState({ id: null, message: "" });
  const fileInputsRef = useRef({});

  const existingMarketplaces = documentsList.filter(
    (d) => d.document_type === doc.document_type
  );

  const handleAddClick = () => {
    setAdding(true);
    setError("");
  };

  const handleCancel = () => {
    setAdding(false);
    setNewMarketplace(EMPTY_MARKETPLACE);
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewMarketplace((prev) => ({ ...prev, [name]: value }));
  };

  /** Save new marketplace (metadata only) */
  const handleSaveMarketplace = async () => {
    if (!newMarketplace.marketplace.trim() || !newMarketplace.country.trim()) {
      setError("Please enter both Marketplace/Gateway Name and Country.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("loan_id", applicationUuid);
      formData.append("document_type_id", doc.document_type);
      formData.append("metadata", JSON.stringify(newMarketplace));

      const res = await uploadDocumentRequest(formData);

      if (res?.success) {
        await reloadDocumentsList();
        setAdding(false);
        setNewMarketplace(EMPTY_MARKETPLACE);
      } else {
        setError("Error saving marketplace. Try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save marketplace info. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadFiles = async (e, marketplaceDoc) => {
    const files = Array.from(e.target.files);

    if (!files.length) {
      setCardError({ id: marketplaceDoc.id, message: "Please select files to upload." });
      return;
    }

    setUploadingId(marketplaceDoc.id);
    onUploadingChange?.(true);
    setCardError({ id: null, message: "" });

    try {
      const failures = await uploadEachFile(files, (file) => {
        const uploadData = new FormData();
        uploadData.append("file", file);
        return uploadDocumentFilesRequest(marketplaceDoc.id, uploadData);
      });
      setCardError({ id: marketplaceDoc.id, message: describeUploadFailures(failures, files.length) });

      // Reset only this specific input
      if (fileInputsRef.current[marketplaceDoc.id]) {
        fileInputsRef.current[marketplaceDoc.id].value = "";
      }
      await reloadDocumentsList();
    } catch (err) {
      console.error(err);
      setCardError({ id: marketplaceDoc.id, message: "File upload failed. Try again." });
    } finally {
      setUploadingId(null);
      onUploadingChange?.(false);
    }
  };

  /** Delete entire marketplace document */
  const handleDeleteMarketplace = async (marketplaceDoc) => {
    const name = marketplaceDoc.metadata?.marketplace || "this marketplace";
    if (!window.confirm(`Delete ${name}? This will remove all statements too.`))
      return;

    try {
      setDeleting(marketplaceDoc.id);
      const res = await deleteDocumentRequest(marketplaceDoc.uuid); // Backend deletes document + files
      if (res?.success === false) {
        setCardError({ id: marketplaceDoc.id, message: "Failed to delete marketplace. Try again." });
        return;
      }
      await reloadDocumentsList();
    } catch (err) {
      console.error(err);
      setCardError({ id: marketplaceDoc.id, message: "Failed to delete marketplace. Try again." });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="mb-4 border rounded p-3">
      <h6>
        {doc.title} {doc.is_mandatory && <span className="text-danger">*</span>}
      </h6>
      <p className="text-muted">{doc.description}</p>

      {/* Existing marketplaces list */}
      {existingMarketplaces.length > 0 ? (
        existingMarketplaces.map((marketplaceDoc) => (
          <Card key={marketplaceDoc.id} className="mb-3 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h6 className="mb-1">{marketplaceDoc.metadata?.marketplace || 'Others'} - <small className="text-muted">{marketplaceDoc.metadata?.country}</small></h6>
                </div>
                {/* 🔴 Delete Icon */}
                <Button
                  variant="link"
                  className="p-0 text-danger"
                  title="Delete marketplace and all statements"
                  onClick={() => handleDeleteMarketplace(marketplaceDoc)}
                  disabled={deleting === marketplaceDoc.id}
                >
                  {deleting === marketplaceDoc.id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <BsTrash size={18} />
                  )}
                </Button>
              </div>

              {/* Upload new statements */}
              <Form.Group className="mt-3">
                <Form.Control
                  className="file-input--hide-chosen-text"
                  type="file"
                  aria-label="Upload statements"
                  multiple
                  onChange={(e) => handleUploadFiles(e, marketplaceDoc)}
                  ref={(el) => (fileInputsRef.current[marketplaceDoc.id] = el)}
                  disabled={!!uploadingId}
                  data-testid="fincobox-upload-marketplace-file-input"
                />
                {uploadingId === marketplaceDoc.id && (
                  <div className="d-flex justify-content-center align-items-center mt-2 text-muted" role="status">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Uploading file...</span>
                  </div>
                )}
                {cardError.id === marketplaceDoc.id && cardError.message && (
                  <p className="text-danger small mt-2 mb-0" role="alert">{cardError.message}</p>
                )}
              </Form.Group>

              {/* Uploaded files */}
              <div className="mt-2">
                {(marketplaceDoc.document_files || []).length > 0 && (
                  <span data-testid="fincobox-upload-marketplace-uploaded" aria-hidden="true" className="visually-hidden">✓</span>
                )}
                <DocumentFiles files={marketplaceDoc.document_files || []} reloadDocumentsList={reloadDocumentsList} />
              </div>
            </Card.Body>
          </Card>
        ))
      ) : (
        <p className="text-muted">No marketplace added yet.</p>
      )}

      {/* Add new marketplace form */}
      {adding ? (
        <Card className="mt-3 p-3 shadow-sm">
          <Row>
            <Col md={5}>
              <Form.Group className="mb-3" controlId="add-marketplace-name">
                <Form.Label>Marketplace/ Payment Gateway Name</Form.Label>
                <Form.Control
                  type="text"
                  name="marketplace"
                  placeholder="Enter Marketplace/ Payment Gateway name"
                  value={newMarketplace.marketplace}
                  onChange={handleChange}
                  data-testid="fincobox-upload-marketplace-name"
                />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group className="mb-3" controlId="add-marketplace-country">
                <Form.Label>Country</Form.Label>
                <Form.Control
                  type="text"
                  name="country"
                  placeholder="Enter Country"
                  value={newMarketplace.country}
                  onChange={handleChange}
                  data-testid="fincobox-upload-marketplace-country"
                />
              </Form.Group>
            </Col>
          </Row>

          {error && <p className="text-danger small">{error}</p>}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveMarketplace} disabled={saving} data-testid="fincobox-upload-save-marketplace">
              {saving ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
          </div>
        </Card>
      ) : (
        <Button variant="outline-primary" onClick={handleAddClick} data-testid="fincobox-upload-add-marketplace">
          + Add Marketplace / Payment Gateway
        </Button>
      )}
    </div>
  );
};

export default MarketplaceStatementsUploadForm;
